import { useCallback } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { useTranslate } from 'src/locales';

import { Iconify } from 'src/components/iconify';

import { useAuthContext } from '../hooks';

// ----------------------------------------------------------------------

type AuthErrorDialogProps = {
  error: Error;
};

export function AuthErrorDialog({ error }: AuthErrorDialogProps) {
  const { t } = useTranslate('auth');

  const { checkUserSession } = useAuthContext();

  const handleRetry = useCallback(async () => {
    try {
      await checkUserSession?.();
    } catch (err) {
      console.error('Retry error:', err);
    }
  }, [checkUserSession]);

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <Dialog open fullScreen disableEscapeKeyDown>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 4,
          textAlign: 'center',
        }}
      >
        {/* Illustration */}
        <Box
          component="img"
          src="/assets/illustrations/characters/character-error.webp"
          alt={t('errorDialog.title')}
          sx={{
            width: 240,
            height: 240,
            mb: 3,
            objectFit: 'contain',
          }}
        />

        {/* Error Icon Badge */}
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: (theme) => alpha(theme.palette.error.main, 0.16),
            mb: 3,
          }}
        >
          <Iconify icon="solar:cloud-cross-bold" width={32} sx={{ color: 'error.main' }} />
        </Box>

        {/* Title */}
        <Typography variant="h4" sx={{ mb: 2 }}>
          {t('errorDialog.title')}
        </Typography>

        {/* Message */}
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, maxWidth: 480 }}>
          {t('errorDialog.message')}
        </Typography>

        {/* Error Details */}
        <Box
          sx={{
            p: 2,
            borderRadius: 1.5,
            bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
            border: (theme) => `1px solid ${alpha(theme.palette.error.main, 0.24)}`,
            mb: 4,
            maxWidth: 480,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Iconify
              icon="solar:info-circle-bold"
              width={20}
              sx={{ color: 'error.main', mt: 0.25 }}
            />
            <Typography variant="body2" color="text.secondary" textAlign="left">
              {t('errorDialog.errorDetail', {
                message: error.message || t('errorDialog.defaultError'),
              })}
            </Typography>
          </Stack>
        </Box>

        {/* Actions */}
        <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
          <Button
            variant="contained"
            size="large"
            color="primary"
            onClick={handleRetry}
            startIcon={<Iconify icon="solar:restart-bold" />}
            sx={{ minWidth: 180 }}
          >
            {t('errorDialog.tryAgain')}
          </Button>

          <Button
            variant="outlined"
            size="large"
            color="inherit"
            onClick={handleReload}
            startIcon={<Iconify icon="solar:refresh-bold" />}
            sx={{ minWidth: 140 }}
          >
            {t('errorDialog.reloadPage')}
          </Button>
        </Stack>

        {/* Footer note */}
        <Typography variant="caption" color="text.disabled" sx={{ mt: 3, maxWidth: 400 }}>
          {t('errorDialog.footerNote')}
        </Typography>
      </Box>
    </Dialog>
  );
}
